import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import dayjs from 'dayjs'
import { FaSave } from 'react-icons/fa'
import { metodoPrograma } from '../../../../components/api'
import { setError, setSuccess } from '../../../../components/alerts'
import CurvaCrescimentoOMS from './CurvaCrescimentoOMS'

const DIAS_POR_MES = 30.4375

const CODIGO = 'CtrlAtendimentoMedico'

const getConfig = (indicadores, idCurva) => {
  const sel = indicadores?.find((c) => c.id_curva_crescimento === idCurva)
  if (!sel)
    return {
      campoPaciente: 'nr_peso',
      unidade: 'kg',
      titulo: 'Peso para Idade',
    }
  if (sel.ds_nome?.toLowerCase().includes('imc'))
    return {
      campoPaciente: 'nr_imc',
      unidade: 'kg/m²',
      titulo: 'IMC para Idade',
    }
  if (
    sel.ds_nome?.toLowerCase().includes('altura') ||
    sel.ds_nome?.toLowerCase().includes('estatura')
  )
    return {
      campoPaciente: 'nr_altura',
      unidade: 'cm',
      titulo: 'Altura para Idade',
    }
  if (
    sel.ds_nome?.toLowerCase().includes('cefálico') ||
    sel.ds_nome?.toLowerCase().includes('cafálica') ||
    sel.ds_nome?.toLowerCase().includes('circunferência')
  )
    return {
      campoPaciente: 'nr_perimetro_cefalico',
      unidade: 'cm',
      titulo: 'Perímetro Cefálico para Idade',
    }
  if (sel.ds_nome?.toLowerCase().includes('peso'))
    return {
      campoPaciente: 'nr_peso',
      unidade: 'kg',
      titulo: 'Peso para Idade',
    }
  return { campoPaciente: 'nr_peso', unidade: 'kg', titulo: sel.ds_nome }
}

const parseData = (dt) => {
  if (!dt) return null
  if (typeof dt === 'string' && dt.includes('/')) {
    const [dia, mes, anoHora] = dt.split('/')
    const [ano] = anoHora.split(' ')
    const d = new Date(`${ano}-${mes}-${dia}T00:00:00`)
    return Number.isNaN(d.getTime()) ? null : d
  }
  const d = new Date(dt)
  return Number.isNaN(d.getTime()) ? null : d
}

const formatarData = (dt) => {
  const d = parseData(dt)
  return d ? d.toLocaleDateString('pt-BR') : ''
}

const calcularIdade = (dtNascimento, dataReferencia = new Date()) => {
  if (!dtNascimento) return ''
  const nasc = parseData(dtNascimento)
  if (!nasc) return ''
  const ref = new Date(dataReferencia)
  let meses =
    (ref.getFullYear() - nasc.getFullYear()) * 12 +
    (ref.getMonth() - nasc.getMonth())
  if (ref.getDate() < nasc.getDate()) meses -= 1
  if (meses < 0) meses = 0
  if (meses < 24) return `${meses} ${meses === 1 ? 'mês' : 'meses'}`
  const anos = Math.floor(meses / 12)
  const resto = meses % 12
  if (resto === 0) return `${anos} ${anos === 1 ? 'ano' : 'anos'}`
  return `${anos}a ${resto}m`
}

const calcularDias = (dtNascimento, dtAvaliacao) => {
  const nasc = parseData(dtNascimento)
  const aval = parseData(dtAvaliacao)
  if (!nasc || !aval) return null
  const diff = aval.getTime() - nasc.getTime()
  return Math.floor(diff / (1000 * 60 * 60 * 24))
}

const parseRangeCurva = (dsNome) => {
  const nome = (dsNome ?? '')
    .toLowerCase()
    .replace(/\be\b/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
  if (/(?:0\s*a|até)\s*13\s*meses/.test(nome))
    return { idadeMin: 0, idadeMax: 13 }
  let m = nome.match(/(\d+)\s*até\s*(\d+)\s*anos/)
  if (m) return { idadeMin: +m[1] * 12, idadeMax: +m[2] * 12 }
  m = nome.match(/até\s*(\d+)\s*anos/)
  if (m) return { idadeMin: 0, idadeMax: +m[1] * 12 }
  m = nome.match(/até\s*(\d+)\s*meses/)
  if (m) return { idadeMin: 0, idadeMax: +m[1] }
  return { idadeMin: 0, idadeMax: 60 }
}

const getTipoCurva = (dsNome) => {
  const nome = (dsNome ?? '').toLowerCase()
  if (nome.includes('imc')) return 'imc'
  if (nome.includes('altura') || nome.includes('estatura')) return 'altura'
  if (
    nome.includes('cefálico') ||
    nome.includes('cafálica') ||
    nome.includes('circunferência')
  )
    return 'perimetro'
  return 'peso'
}

const corClassificacao = (classe) => {
  if (classe.includes('Muito baixo') || classe.includes('Muito elevado'))
    return 'error'
  if (classe.includes('Baixo') || classe.includes('Elevado')) return 'warning'
  return 'success'
}

const classificar = (valor, ref, tpCurva) => {
  if (!ref) return '-'
  if (tpCurva === 'P') {
    if (valor < ref.nr_percentil_3) return 'Muito baixo (< P3)'
    if (valor < ref.nr_percentil_15) return 'Baixo (P3 a P15)'
    if (valor <= ref.nr_percentil_85) return 'Adequado (P15 a P85)'
    if (valor <= ref.nr_percentil_97) return 'Elevado (P85 a P97)'
    return 'Muito elevado (> P97)'
  }
  if (valor < ref.nr_desvio_menos_3) return 'Muito baixo (< -3 DP)'
  if (valor < ref.nr_desvio_menos_2) return 'Baixo (-3 a -2 DP)'
  if (valor <= ref.nr_desvio_mais_2) return 'Adequado (-2 a +2 DP)'
  if (valor <= ref.nr_desvio_mais_3) return 'Elevado (+2 a +3 DP)'
  return 'Muito elevado (> +3 DP)'
}

export const CurvaCrescimento = ({ atendimentoSelecionado }) => {
  const paciente = atendimentoSelecionado?.dadosAtendimento?.[7]
  const idPaciente = paciente?.idPaciente
  const nmPaciente = paciente?.nmPaciente
  const dtNascimento = paciente?.dtNascimento
  const tpSexo =
    typeof paciente?.tpSexo === 'string'
      ? paciente?.tpSexo
      : paciente?.tpSexo?.tpSexo

  const [idCurva, setIdCurva] = useState(1)
  const [tpCurva, setTpCurva] = useState('Z')
  const [indicadores, setIndicadores] = useState([])
  const [valores, setValores] = useState([])
  const [valoresPorCurva, setValoresPorCurva] = useState({})
  const [medicoes, setMedicoes] = useState([])
  const [carregando, setCarregando] = useState(false)
  const [salvando, setSalvando] = useState(false)

  const [dtAvaliacao, setDtAvaliacao] = useState(dayjs().format('YYYY-MM-DD'))
  const [nrPeso, setNrPeso] = useState('')
  const [nrAltura, setNrAltura] = useState('')
  const [nrPerimetroCefalico, setNrPerimetroCefalico] = useState('')

  const nrIdadeDias = useMemo(
    () => calcularDias(dtNascimento, dtAvaliacao),
    [dtNascimento, dtAvaliacao],
  )

  const config = useMemo(
    () => getConfig(indicadores, idCurva),
    [indicadores, idCurva],
  )

  const idadeMesesPaciente = useMemo(() => {
    const ultimaMedicao = medicoes?.[medicoes.length - 1]
    const dias =
      ultimaMedicao?.nr_idade_dias ?? calcularDias(dtNascimento, new Date())
    return dias == null ? null : dias / DIAS_POR_MES
  }, [dtNascimento, medicoes])

  const jaAutoSelecionou = useRef(false)

  const curvaCobreIdade = (curva, meses) => {
    if (meses == null) return true
    const range = parseRangeCurva(curva?.ds_nome)
    return meses >= range.idadeMin && meses <= range.idadeMax
  }

  useEffect(() => {
    if (jaAutoSelecionou.current || !indicadores.length) return
    const atual = indicadores.find((c) => c.id_curva_crescimento === idCurva)
    if (!curvaCobreIdade(atual, idadeMesesPaciente)) {
      const adequada = indicadores.find((c) =>
        curvaCobreIdade(c, idadeMesesPaciente),
      )
      if (adequada) setIdCurva(adequada.id_curva_crescimento)
    }
    jaAutoSelecionou.current = true
  }, [indicadores, idCurva, idadeMesesPaciente])

  const carregarIndicadores = () => {
    metodoPrograma('listaCurvaCrescimento', {}, CODIGO).then((res) => {
      setIndicadores(res?.lista || [])
    })
  }

  const carregarDados = useCallback(async () => {
    if (!idPaciente) return
    setCarregando(true)
    const respMedicoes = await metodoPrograma(
      'curvaCrescimentoPaciente',
      { idPaciente },
      CODIGO,
    )
    setMedicoes(respMedicoes?.lista || [])
    setCarregando(false)
  }, [idPaciente])

  const carregarTodasCurvas = useCallback(async () => {
    if (!idPaciente || !tpSexo || !indicadores.length) return
    setCarregando(true)
    const respostas = await Promise.all(
      indicadores.map((c) =>
        metodoPrograma(
          'valoresCurvaCrescimento',
          { idCurvaCrescimento: c.id_curva_crescimento, tpSexo, tpCurva },
          CODIGO,
        ),
      ),
    )
    const mapa = {}
    respostas.forEach((res, i) => {
      mapa[indicadores[i].id_curva_crescimento] = res?.lista || []
    })
    setValoresPorCurva(mapa)
    setCarregando(false)
  }, [idPaciente, tpSexo, tpCurva, indicadores])

  useEffect(() => {
    carregarIndicadores()
  }, [])

  useEffect(() => {
    carregarDados()
  }, [idPaciente, carregarDados])

  useEffect(() => {
    carregarTodasCurvas()
  }, [tpSexo, tpCurva, indicadores, carregarTodasCurvas])

  useEffect(() => {
    setValores(valoresPorCurva[idCurva] || [])
  }, [idCurva, valoresPorCurva])

  const limparFormulario = () => {
    setDtAvaliacao(dayjs().format('YYYY-MM-DD'))
    setNrPeso('')
    setNrAltura('')
    setNrPerimetroCefalico('')
  }

  const salvar = async () => {
    if (!idPaciente) {
      setError('Paciente não identificado')
      return
    }
    if (!tpSexo) {
      setError('Paciente não possui sexo informado')
      return
    }
    if (!dtAvaliacao) {
      setError('Informe a data da avaliação')
      return
    }
    if (nrIdadeDias == null || nrIdadeDias < 0) {
      setError('Data de avaliação inválida em relação à data de nascimento')
      return
    }
    if (!nrPeso && !nrAltura && !nrPerimetroCefalico) {
      setError(
        'Informe pelo menos um valor (peso, altura ou perímetro cefálico)',
      )
      return
    }

    setSalvando(true)
    const res = await metodoPrograma(
      'salvarCurvaCrescimentoPaciente',
      {
        idPaciente,
        dtAvaliacao,
        nrPeso: nrPeso ? parseFloat(nrPeso) : null,
        nrAltura: nrAltura ? parseFloat(nrAltura) : null,
        nrPerimetroCefalico: nrPerimetroCefalico
          ? parseFloat(nrPerimetroCefalico)
          : null,
      },
      CODIGO,
    )
    setSalvando(false)

    if (res.status) {
      setSuccess('Medição salva com sucesso')
      limparFormulario()
      carregarDados()
    } else {
      setError(res.dsErro || 'Erro ao salvar medição')
    }
  }

  const campoMediana = tpCurva === 'P' ? 'nr_percentil_50' : 'nr_mediana'
  const campoInf = tpCurva === 'P' ? 'nr_percentil_3' : 'nr_desvio_menos_2'
  const campoSup = tpCurva === 'P' ? 'nr_percentil_97' : 'nr_desvio_mais_2'

  const buscarRef = useCallback(
    (mesProximo) => {
      const listaSel = valoresPorCurva[idCurva] || []
      const refSel = listaSel.find((v) => v.nr_idade_meses === mesProximo)
      if (refSel) return refSel
      const sel = indicadores.find((c) => c.id_curva_crescimento === idCurva)
      const tipo = getTipoCurva(sel?.ds_nome)
      const candidatas = indicadores
        .filter(
          (c) =>
            c.id_curva_crescimento !== idCurva &&
            getTipoCurva(c.ds_nome) === tipo,
        )
        .sort((a, b) => {
          const ra = parseRangeCurva(a.ds_nome)
          const rb = parseRangeCurva(b.ds_nome)
          return rb.idadeMax - rb.idadeMin - (ra.idadeMax - ra.idadeMin)
        })
      for (const c of candidatas) {
        const range = parseRangeCurva(c.ds_nome)
        if (mesProximo >= range.idadeMin && mesProximo <= range.idadeMax) {
          const refAlt = (valoresPorCurva[c.id_curva_crescimento] || []).find(
            (v) => v.nr_idade_meses === mesProximo,
          )
          if (refAlt) return refAlt
        }
      }
      return null
    },
    [valoresPorCurva, idCurva, indicadores],
  )

  const linhasTabela = useMemo(() => {
    return medicoes
      .map((p) => {
        const idadeDias =
          p.nr_idade_dias ?? calcularDias(dtNascimento, p.dt_avaliacao)
        const idadeMeses = idadeDias == null ? null : idadeDias / DIAS_POR_MES
        const mesProximo = idadeMeses == null ? null : Math.round(idadeMeses)
        const ref = mesProximo == null ? null : buscarRef(mesProximo)
        const valor = p[config.campoPaciente]
        const classe = classificar(valor, ref, tpCurva)
        return { p, idadeMeses, ref, valor, classe }
      })
      .filter((l) => l.valor != null)
  }, [medicoes, config, tpCurva, dtNascimento, buscarRef])

  const refsPorMedicao = useMemo(() => {
    const mapa = {}
    medicoes.forEach((p) => {
      const idadeDias =
        p.nr_idade_dias ?? calcularDias(dtNascimento, p.dt_avaliacao)
      if (idadeDias == null) return
      const mesProximo = Math.round(idadeDias / DIAS_POR_MES)
      const ref = buscarRef(mesProximo)
      if (ref) mapa[p.id_curva_crescimento_paciente] = ref
    })
    return mapa
  }, [medicoes, dtNascimento, buscarRef])

  if (!idPaciente) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
        Paciente não identificado para este atendimento.
      </Paper>
    )
  }

  if (!tpSexo || (tpSexo !== 'M' && tpSexo !== 'F')) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
        O paciente não possui sexo compatível com as curvas OMS
        (Masculino/Feminino).
      </Paper>
    )
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Curva de Crescimento (OMS)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Valores de referência OMS (0-5 anos) comparados às medições do paciente.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <Typography variant="body2">
              <strong>Paciente:</strong>{' '}
              {nmPaciente
                ? `${nmPaciente} · ${calcularIdade(dtNascimento)}`
                : '-'}
            </Typography>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Indicador</InputLabel>
              <Select
                value={idCurva}
                label="Indicador"
                onChange={(e) => setIdCurva(e.target.value)}
              >
                {indicadores.map((c) => (
                  <MenuItem
                    key={c.id_curva_crescimento}
                    value={c.id_curva_crescimento}
                  >
                    {c.ds_nome}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <ToggleButtonGroup
              value={tpCurva}
              exclusive
              onChange={(e, v) => v && setTpCurva(v)}
              size="small"
              fullWidth
            >
              <ToggleButton value="Z">Z-Score</ToggleButton>
              <ToggleButton value="P">Percentil</ToggleButton>
            </ToggleButtonGroup>
          </Grid>
          <Grid item xs={12} md={2}>
            <Typography
              variant="body2"
              color="text.secondary"
              textAlign="right"
            >
              {tpSexo === 'M' ? 'Masculino' : 'Feminino'}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Nova Medição
        </Typography>
        <Grid container spacing={2} alignItems="flex-start">
          <Grid item xs={12} md={2}>
            <TextField
              label="Data da Avaliação"
              type="date"
              size="small"
              fullWidth
              value={dtAvaliacao}
              onChange={(e) => setDtAvaliacao(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              label="Idade (dias)"
              size="small"
              fullWidth
              value={nrIdadeDias ?? ''}
              disabled
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              label="Peso"
              size="small"
              fullWidth
              type="number"
              value={nrPeso}
              onChange={(e) => setNrPeso(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">kg</InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              label="Altura"
              size="small"
              fullWidth
              type="number"
              value={nrAltura}
              onChange={(e) => setNrAltura(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">cm</InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              label="Perímetro Cefálico"
              size="small"
              fullWidth
              type="number"
              value={nrPerimetroCefalico}
              onChange={(e) => setNrPerimetroCefalico(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">cm</InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              variant="contained"
              fullWidth
              size="small"
              startIcon={salvando ? <CircularProgress size={14} /> : <FaSave />}
              onClick={salvar}
              disabled={salvando}
            >
              Salvar
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {carregando ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Paper sx={{ p: 3, mb: 4 }}>
            <CurvaCrescimentoOMS
              valores={valores}
              paciente={medicoes}
              campoPaciente={config.campoPaciente}
              tpCurva={tpCurva}
              titulo={`${config.titulo} - ${tpSexo === 'M' ? 'Menino' : 'Menina'}`}
              unidade={config.unidade}
              dtNascimento={dtNascimento}
              refsPorMedicao={refsPorMedicao}
            />
          </Paper>

          <Typography variant="h6" gutterBottom>
            Valores do paciente x Referência OMS
          </Typography>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Data</TableCell>
                  <TableCell align="right">Idade (meses)</TableCell>
                  <TableCell align="right">
                    Paciente ({config.unidade})
                  </TableCell>
                  <TableCell align="right">
                    {tpCurva === 'P' ? 'P50' : 'Mediana'}
                  </TableCell>
                  <TableCell align="right">
                    {tpCurva === 'P' ? 'P3' : '-2 DP'}
                  </TableCell>
                  <TableCell align="right">
                    {tpCurva === 'P' ? 'P97' : '+2 DP'}
                  </TableCell>
                  <TableCell>Classificação</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {linhasTabela.map(({ p, idadeMeses, ref, valor, classe }) => (
                  <TableRow key={p.id_curva_crescimento_paciente}>
                    <TableCell>{formatarData(p.dt_avaliacao)}</TableCell>
                    <TableCell align="right">
                      {idadeMeses == null ? '-' : idadeMeses.toFixed(1)}
                    </TableCell>
                    <TableCell align="right">
                      <strong>{valor}</strong>
                    </TableCell>
                    <TableCell align="right">
                      {ref ? ref[campoMediana] : '-'}
                    </TableCell>
                    <TableCell align="right">
                      {ref ? ref[campoInf] : '-'}
                    </TableCell>
                    <TableCell align="right">
                      {ref ? ref[campoSup] : '-'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={classe}
                        color={corClassificacao(classe)}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  )
}

export default CurvaCrescimento
